/**
 * Line each desktop dropdown up under the item that opened it.
 *
 * Horizon's desktop submenu is a full-width mega menu whose columns start at
 * the page margin, so Clothing's links open well to the left of Clothing.
 * The panel, its open animation and the hover bridge stay as Horizon built
 * them; this only publishes `--ll-submenu-offset` on each submenu, which
 * ll-custom.css uses to slide the link columns under the trigger. The offset
 * is clamped so the last column never passes the page margin.
 *
 * Recalculates when the header changes width, once web fonts land, when the
 * theme editor re-renders the header, and again as the pointer or keyboard
 * focus enters the menu. That last pass runs before any submenu is shown, so
 * the offset is right even if an earlier pass measured an unsettled layout.
 */
const DESKTOP = window.matchMedia('(min-width: 990px)');

/** @param {Element} menu */
function align(menu) {
  for (const item of menu.querySelectorAll('.menu-list__list-item:not([slot="overflow"])')) {
    const submenu = item.querySelector(':scope > .menu-list__submenu');
    const title = item.querySelector('.menu-list__link-title');
    const grid = submenu?.querySelector('.mega-menu__grid');
    const list = grid?.querySelector('.mega-menu__list');
    if (!(submenu instanceof HTMLElement) || !title || !grid || !list) continue;

    if (!DESKTOP.matches) {
      submenu.style.removeProperty('--ll-submenu-offset');
      continue;
    }

    // Measure with the offset removed so the columns report their natural
    // positions. Submenus are visibility: hidden when closed, which still
    // lays them out, so this works before anything opens.
    submenu.style.removeProperty('--ll-submenu-offset');
    const gridBox = grid.getBoundingClientRect();
    let contentRight = gridBox.left;
    for (const column of list.children) {
      contentRight = Math.max(contentRight, column.getBoundingClientRect().right);
    }

    const wanted = title.getBoundingClientRect().left - gridBox.left;
    const room = gridBox.right - contentRight;
    const offset = Math.round(Math.max(0, Math.min(wanted, room)));
    if (offset > 0) submenu.style.setProperty('--ll-submenu-offset', `${offset}px`);
  }
}

function alignAll() {
  document.querySelectorAll('header-menu').forEach(align);
}

let queued = false;
function queueAlign() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    alignAll();
  });
}

const resizeObserver = new ResizeObserver(queueAlign);

/** @type {WeakSet<Element>} */
const watched = new WeakSet();

function observeMenus() {
  for (const menu of document.querySelectorAll('header-menu')) {
    if (watched.has(menu)) continue;
    watched.add(menu);
    resizeObserver.observe(menu);
    menu.addEventListener('pointerenter', () => align(menu));
    menu.addEventListener('focusin', () => align(menu));
  }
  queueAlign();
}

observeMenus();
document.fonts?.ready.then(queueAlign);
DESKTOP.addEventListener('change', queueAlign);
document.addEventListener('shopify:section:load', observeMenus);
